import apiClient from "../utils/apiClient";
import {
  WorkflowArchiveRequestDto,
  WorkflowArchiveResultDto,
  WorkflowDefinitionListItemDto,
  WorkflowDefinitionRefRequestDto,
  WorkflowDefinitionsListResultDto,
  WorkflowDuplicateRequestDto,
  WorkflowDuplicateResultDto,
  WorkflowGraphDto,
  WorkflowPublishRequestDto,
  WorkflowPublishResultDto,
  WorkflowSaveRequestDto,
  WorkflowSaveResultDto,
  WorkflowSettingsRequestDto,
  WorkflowSettingsResultDto,
  WorkflowValidateResultDto,
  WorkflowValidationIssueDto,
  WorkflowWebservicePreviewRequestDto,
  WorkflowWebservicePreviewResultDto,
} from "../types/workflowDefinitionDto";

/**
 * A Builder write, with the two outcomes the screen renders itself resolved as data:
 * - `conflict` (409) — stale rowVersionTag, the draft lock held by someone else, or the row is no
 *   longer a draft. The body is the endpoint's own result DTO (e.g. save's `lockedByUserId`).
 * - `invalid` (422) — the API's `{ error, issues[] }` (or `{ error, message }`) body.
 *
 * Every other failure is rethrown untouched so useApi's central alert still handles it — the same
 * opt-in split as readPostIfWrite.
 */
export type WorkflowWriteOutcome<T> =
  | { kind: "ok"; data: T }
  | { kind: "conflict"; data: Partial<T> }
  | { kind: "invalid"; error: string; issues: WorkflowValidationIssueDto[] };

/** Read structurally, not through axios.isAxiosError — see pifWriteResult.ts for why. */
interface HttpErrorLike {
  response?: { status?: number; data?: unknown };
}

interface UnprocessableBody {
  error?: string;
  message?: string;
  issues?: WorkflowValidationIssueDto[];
}

export const readWorkflowWrite = async <T>(
  call: () => Promise<{ data: T }>,
): Promise<WorkflowWriteOutcome<T>> => {
  try {
    const response = await call();
    return { kind: "ok", data: response.data };
  } catch (error) {
    const response = (error as HttpErrorLike | null)?.response;
    if (response?.status === 409) {
      return { kind: "conflict", data: (response.data ?? {}) as Partial<T> };
    }
    if (response?.status === 422) {
      const body = (response.data ?? {}) as UnprocessableBody;
      const code = body.error ?? "VALIDATION_FAILED";
      const issues =
        body.issues && body.issues.length > 0
          ? body.issues
          : [{ code, message: body.message ?? code }];
      return { kind: "invalid", error: code, issues };
    }
    throw error;
  }
};

export const getWorkflowDefinitionGraph = async (id: number): Promise<WorkflowGraphDto> => {
  const response = await apiClient.get<WorkflowGraphDto>(`/workflow/definition?id=${id}`);
  return response.data;
};

/** Whole-graph upsert — the only way the Builder persists stages/fields/transitions. */
export const saveWorkflowDefinition = (
  request: WorkflowSaveRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowSaveResultDto>> =>
  readWorkflowWrite(() => apiClient.post<WorkflowSaveResultDto>("/workflow/definition/save", request));

/** A failed checklist is still a 200 (`isValidated: false` + `issues[]`). */
export const validateWorkflowDefinition = (
  request: WorkflowDefinitionRefRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowValidateResultDto>> =>
  readWorkflowWrite(() =>
    apiClient.post<WorkflowValidateResultDto>("/workflow/definition/validate", request),
  );

export const publishWorkflowDefinition = (
  request: WorkflowPublishRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowPublishResultDto>> =>
  readWorkflowWrite(() =>
    apiClient.post<WorkflowPublishResultDto>("/workflow/definition/publish", request),
  );

export const saveWorkflowSettings = (
  request: WorkflowSettingsRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowSettingsResultDto>> =>
  readWorkflowWrite(() =>
    apiClient.post<WorkflowSettingsResultDto>("/workflow/definition/settings", request),
  );

/** The API caps pageSize at 100. */
const LIBRARY_PAGE_SIZE = 100;

/**
 * Every workflow family (its latest version), across all statuses. The Library filters and counts
 * client-side over the whole list (Epic 80428), so this keeps paging until it has `total` rows
 * rather than handing back one page. The pages are sequential — the first one is what says how
 * many there are.
 */
export const getWorkflowDefinitions = async (): Promise<WorkflowDefinitionListItemDto[]> => {
  const items: WorkflowDefinitionListItemDto[] = [];
  let page = 1;

  for (;;) {
    const response = await apiClient.get<WorkflowDefinitionsListResultDto>("/workflow/definitions", {
      params: { status: "all", latestOnly: true, page, pageSize: LIBRARY_PAGE_SIZE },
    });
    const pageItems = response.data.items ?? [];
    items.push(...pageItems);

    // An empty page also stops the loop, so a total that shrank mid-read can't spin forever.
    if (pageItems.length === 0 || items.length >= response.data.total) {
      return items;
    }
    page += 1;
  }
};

export const duplicateWorkflowDefinition = (
  request: WorkflowDuplicateRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowDuplicateResultDto>> =>
  readWorkflowWrite(() =>
    apiClient.post<WorkflowDuplicateResultDto>("/workflow/definition/duplicate", request),
  );

export const archiveWorkflowDefinition = (
  request: WorkflowArchiveRequestDto,
): Promise<WorkflowWriteOutcome<WorkflowArchiveResultDto>> =>
  readWorkflowWrite(() =>
    apiClient.post<WorkflowArchiveResultDto>("/workflow/definition/archive", request),
  );

/**
 * A dry run's outcome. `result` covers the remote's answer either way — a remote timeout or 5xx
 * is still a 200 with `ok: false`. `refused` is the API declining to make the call at all: a bad
 * config (400), a URL off the https allow-list (422) or the rate limit (429), each with a message
 * the panel shows as-is.
 */
export type WorkflowPreviewOutcome =
  | { kind: "result"; data: WorkflowWebservicePreviewResultDto }
  | { kind: "refused"; status: number; message: string };

const PREVIEW_REFUSAL_STATUSES = [400, 422, 429];

/** Dry run only — never advances a real case. */
export const previewWorkflowWebservice = async (
  request: WorkflowWebservicePreviewRequestDto,
): Promise<WorkflowPreviewOutcome> => {
  try {
    const response = await apiClient.post<WorkflowWebservicePreviewResultDto>(
      "/workflow/webservice/preview",
      request,
    );
    return { kind: "result", data: response.data };
  } catch (error) {
    const response = (error as HttpErrorLike | null)?.response;
    if (response?.status && PREVIEW_REFUSAL_STATUSES.includes(response.status)) {
      const message =
        typeof response.data === "string" && response.data.trim()
          ? response.data
          : "The API refused to run this call.";
      return { kind: "refused", status: response.status, message };
    }
    throw error;
  }
};
